import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppState } from '../../../AppStateProvider';
import { getFaviconUrl } from '../../../services/favicon';
import getTrackingLocation from '../../../services/tracking/getTrackingLocation';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

import { MultipleDomainsDropdownOption } from './MultipleDomainsDropdownOption';
import {
  StyledMultipleDomainsContainer,
  StyledMultipleDomainsButtonText,
  StyledExternalMultipleDomainsContainer,
  StyledActionButton,
  StyledCompanyLogo,
  StyledMultipleDomainsDropdown,
  StyledDropdownButtonIcon,
} from './styles';
import { useMultipleDomainsContainer } from './useMultipleDomainsContainer';
import { useMultipleDomainsVariant } from './useMultipleDomainsVariant';

export const MultipleDomainsContainer = () => {
  const { t } = useTranslation();
  const { url } = useAppState();

  const trackingLocation = useMemo(() => getTrackingLocation(url), [url]);

  const { selectedDomain, selectedDomainName, isDropdownOpened, setIsDropdownOpened, domainOptions, onSelectDomain } =
    useMultipleDomainsContainer();
  const { ComponentVariant, dropdownButtonText, isVisible, dropdownWidth } = useMultipleDomainsVariant();

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleTrackEvent = (action: string, eventName: string) => {
    dispatchTrackEventWithLocation('company selection', action, eventName);
  };

  const handleOpenDropdown = () => {
    handleTrackEvent('open companies dropdown', `see companies in meeting/${trackingLocation}`);
    setIsDropdownOpened(!isDropdownOpened);
  };

  const handleSelectDomain = (domain: string) => {
    onSelectDomain(domain);
    handleTrackEvent('select company', `view different company/${trackingLocation}`);
  };

  if (!selectedDomain || !isVisible(domainOptions.length)) {
    return null;
  }

  return (
    <StyledMultipleDomainsContainer
      isOneCompany={domainOptions.length === 1}
      data-automation="MultipleDomainsContainer"
    >
      <ComponentVariant
        selectedDomain={selectedDomain}
        selectedDomainName={selectedDomainName}
        domainsAmount={domainOptions.length}
      />
      {domainOptions.length > 1 ? (
        <StyledExternalMultipleDomainsContainer>
          {dropdownButtonText ? (
            <StyledMultipleDomainsButtonText data-automation="MeetingExternalText">
              {t(dropdownButtonText)}
            </StyledMultipleDomainsButtonText>
          ) : null}
          <StyledActionButton
            onClick={handleOpenDropdown}
            isActive={isDropdownOpened}
            data-automation="MultipleDomainsButton"
            width={dropdownWidth}
          >
            {selectedDomain ? (
              <StyledCompanyLogo isSmall data-automation="MultipleDomainsCompanyLogo">
                <img src={getFaviconUrl(selectedDomain, 10)} width="10px" height="10px" alt={selectedDomain} />
              </StyledCompanyLogo>
            ) : null}
            {selectedDomainName}
            <StyledDropdownButtonIcon iconName={isDropdownOpened ? 'chev-up' : 'chev-down'} size="xs" />
          </StyledActionButton>
          <StyledMultipleDomainsDropdown
            isOpened={isDropdownOpened}
            onClose={() => setIsDropdownOpened(false)}
            minWidth={dropdownWidth}
            placement="right"
            data-automation="MeetingDd"
          >
            {domainOptions.map((option) => (
              <MultipleDomainsDropdownOption
                key={option.domain}
                option={option}
                onSelectDomain={handleSelectDomain}
                isSelected={option.domain === selectedDomain}
                data-automation="MultipleDomainsDdOption"
              />
            ))}
          </StyledMultipleDomainsDropdown>
        </StyledExternalMultipleDomainsContainer>
      ) : null}
    </StyledMultipleDomainsContainer>
  );
};
